import { useCallback, useMemo } from 'react'
import { useWidget } from '../../providers/widget'
import type { GalleryWidgetConfig } from './config'
import { useFetchGalleryImages, type PropertyImage } from './data'
import {
  buildTourSections,
  galleryIndexToTourIndex,
  groupPhotosByRoom,
  parseRoomOrder,
  parseRoomOverrides,
  type TourSection,
} from './rooms'

/**
 * The gallery's photos in both index spaces (#1694).
 *
 * - `images`: gallery order, what the hero grid and the carousel show (the
 *   operator's curated opening photos; the toolkit keeps these as-is too).
 * - `tour`: with `rooms.groupByRoom` on and at least one photo carrying a
 *   room, one section per room in display order; otherwise null.
 * - `ordered`: the order the "Show all photos" browser and the lightbox walk
 *   — the sections flattened, or `images` itself when there is no tour.
 * - `toTourIndex`: where a gallery-order photo sits in `ordered`.
 *
 * Reads the same React Query entry every gallery component already reads, so
 * it adds no request.
 */
export function useGalleryTour(): {
  images: PropertyImage[]
  tour: TourSection<PropertyImage>[] | null
  ordered: PropertyImage[]
  toTourIndex: (galleryIndex: number) => number
} {
  const { options } = useWidget<GalleryWidgetConfig>()
  const { data } = useFetchGalleryImages(options.filter)
  const images = useMemo(() => data?.pages?.flatMap((page) => page.result || []) || [], [data])
  const { groupByRoom, roomOrder, roomOverrides, moreTitle } = options.rooms

  const tour = useMemo(() => {
    if (!groupByRoom) return null
    const groups = groupPhotosByRoom(images, parseRoomOrder(roomOrder), parseRoomOverrides(roomOverrides), { more: moreTitle })
    return groups ? buildTourSections(groups) : null
  }, [images, groupByRoom, roomOrder, roomOverrides, moreTitle])

  const ordered = useMemo(() => (tour ? tour.flatMap((section) => section.photos) : images), [tour, images])

  const toTourIndex = useCallback(
    (galleryIndex: number) => galleryIndexToTourIndex(images, ordered, galleryIndex),
    [images, ordered],
  )

  return { images, tour, ordered, toTourIndex }
}
